import React from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Pagination } from '@/interfaces';

type QueryFn<TPage extends Pagination> = (ctx: {
	pageParam?: number;
}) => Promise<TPage>;

interface Props<TPage extends Pagination> {
	queryKey: string | string[] | unknown[];
	queryFn: QueryFn<TPage>;
	enabled?: boolean;
}

export function useInfinityPagination<TPage extends Pagination>({
	queryKey,
	queryFn,
	enabled = true,
}: Props<TPage>) {
	const {
		data,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
		isLoading,
		refetch,
		isError,
	} = useInfiniteQuery({
		queryKey: Array.isArray(queryKey) ? queryKey : [queryKey],
		queryFn: queryFn,
		initialPageParam: 1,
		enabled,
		getNextPageParam: (lastPage) => {
			return lastPage.page < lastPage.totalPages
				? lastPage.page + 1
				: undefined;
		},
	});

	const observer = React.useRef<IntersectionObserver | null>(null);
	const lastItemCallback = React.useCallback(
		(node: HTMLDivElement | HTMLLIElement | null) => {
			if (isFetchingNextPage) return;

			if (observer.current) observer.current.disconnect();
			observer.current = new IntersectionObserver((entries) => {
				if (entries[0].isIntersecting && hasNextPage) {
					fetchNextPage();
				}
			});

			if (node) observer.current.observe(node);
		},
		[isFetchingNextPage, hasNextPage, fetchNextPage]
	);

	React.useEffect(() => {
		return () => {
			if (observer.current) observer.current.disconnect();
		};
	}, []);

	const total = data?.pages[0].total ?? 0;
	const totalItems = data?.pages[0].totalItems ?? 0;

	return {
		data,
		total,
		refetch,
		isLoading,
		totalItems,
		lastItemCallback,
		hasNextPage,
		fetchNextPage,
		isError,
		isFetchingNextPage,
	};
}
